@if (loading() && !events().length) {
  <div class="consent-history-message">Loading consents...</div>
} @else if (failed() && !events().length) {
  <div class="consent-history-message">Unable to load the consents. Try again later.</div>
} @else if (!events().length) {
  <div class="consent-history-message">No consents recorded.</div>
}

@for (event of events(); track event.key) {
  <article class="consent-event">
    <header class="consent-event-header">
      <span class="consent-event-date">{{ event.respondedAt | dateFormat: 'MM/dd/yy HH:mm' }}</span>
      <span class="consent-event-status" [class.declined]="!event.accepted">{{ event.accepted ? 'Accepted' : 'Declined' }}</span>
      @if (event.withdrawal === 'FULL') {
        <span class="consent-event-status withdrawn">Withdrawn</span>
      } @else if (event.withdrawal === 'PARTIAL') {
        <span class="consent-event-status withdrawn">Partly withdrawn</span>
      }
    </header>

    @if (event.checkboxText) {
      <div class="consent-event-label">Text shown</div>
      <blockquote class="consent-event-text">{{ event.checkboxText.text }}</blockquote>
      <div class="consent-event-muted">{{ event.checkboxText.documentId }}, version {{ event.checkboxText.version }}</div>
    } @else if (event.isLegacy) {
      <div class="consent-event-note">Recorded before consent events, so the exact text shown was not captured.</div>
    }

    @if (event.notice) {
      <div class="consent-event-label">Notice shown</div>
      <div class="consent-event-document">
        <span class="consent-document-name">{{ event.notice.documentName }}</span>
        <span class="consent-event-muted">Version {{ event.notice.version }}</span>
      </div>
    }

    @if (event.documents.length) {
      <div class="consent-event-label">Documents</div>
    }
    @for (document of event.documents; track document.acceptanceId) {
      <div class="consent-event-document">
        <span class="consent-document-name">
          {{ document.isCheckboxText ? 'Checkbox text' : document.documentName }}
        </span>
        <span class="consent-event-muted">Version {{ document.version }}</span>
        @if (document.financialAccountId) {
          <span class="consent-event-muted">Financial account {{ document.financialAccountId }}</span>
        }
        @if (document.phoneNumber) {
          <span class="consent-event-muted">Phone number {{ document.phoneNumber }}</span>
        }
        @if (document.workflowId) {
          <span class="consent-event-muted">Workflow {{ document.workflowId }}</span>
        }
        @if (document.withdrawnAt) {
          <span class="consent-document-withdrawn">
            Withdrawn {{ document.withdrawnAt | dateFormat: 'MM/dd/yy HH:mm' }}
            @if (document.withdrawalChannel) {
              via {{ document.withdrawalChannel }}
            }
          </span>
        }
        <span class="consent-document-actions">
          @if (document.canPreview && document.downloadUrl) {
            <button
              rktButton
              btnType="link"
              type="button"
              class="consent-document-preview"
              [attr.aria-label]="'View ' + document.documentName"
              (click)="onPreview(document)"
            >
              View
            </button>
          }
          @if (document.downloadUrl) {
            <button
              rktButton
              btnType="link"
              type="button"
              class="consent-document-download"
              [attr.aria-label]="'Download ' + document.documentName"
              (click)="onDownload(document)"
            >
              Download
            </button>
          }
        </span>
      </div>
    }

    <div class="consent-event-evidence">
      <app-details-item [showPlaceholder]="false" label="Method" [value]="event.consentMethod" />
      <app-details-item [showPlaceholder]="false" label="Language" [value]="event.language" />
      <app-details-item [showPlaceholder]="false" label="Jurisdiction" [value]="event.jurisdictions.join(', ')" />
      <app-details-item [showPlaceholder]="false" label="IP Address" [value]="event.ipAddress" />
      <app-details-item [showPlaceholder]="false" label="User Agent" [value]="event.userAgent" [wordWrap]="true" />
      <app-details-item [showPlaceholder]="false" label="Consent" [value]="event.consentDefinitionId" />
      <app-details-item [showPlaceholder]="false" label="Placement" [value]="event.placement" />
      @if (isWorkflowView) {
        <app-details-item [showPlaceholder]="false" label="Acceptor" [value]="event.acceptorName" />
        <app-details-item [showPlaceholder]="false" label="Email" [value]="event.acceptorEmail" />
        <app-details-item [showPlaceholder]="false" label="Phone" [value]="event.acceptorPhone" />
      } @else {
        <app-details-item [showPlaceholder]="false" label="Workflow" [contentValue]="true" [value]="event.workflowId">
          @if (event.workflowId) {
            <a class="consent-event-workflow-link" [routerLink]="['/app/linx/linx-requests', event.workflowId]">{{ event.workflowId }}</a>
          } @else {
            N/A
          }
        </app-details-item>
      }
    </div>

    @if (event.details) {
      <div class="consent-event-label">Consent details</div>
      <div class="consent-event-details">
        @for (detail of event.details; track $index) {
          <app-details-item [showPlaceholder]="false" [label]="detail.label" [value]="detail.value" [wordWrap]="true" />
        }
      </div>
    }

    @if (event.hashes.length) {
      <details class="consent-event-hashes">
        <summary>SHA-256 hashes</summary>
        @for (hash of event.hashes; track $index) {
          <div class="consent-event-hash">
            <span class="consent-event-hash-label">{{ hash.label }}</span>
            <code class="consent-event-hash-value" [title]="hash.sha256">{{ shortHash(hash.sha256) }}</code>
            <button
              type="button"
              class="consent-event-hash-copy"
              title="Copy hash"
              [attr.aria-label]="'Copy hash of ' + hash.label"
              [cdkCopyToClipboard]="hash.sha256"
              (cdkCopyToClipboardCopied)="onHashCopied()"
            >
              <rkt-icon name="copy" />
            </button>
          </div>
        }
      </details>
    }

    @if (event.legacyContext) {
      <details class="consent-event-legacy">
        <summary>Recorded context</summary>
        <pre class="consent-event-legacy-context">{{ event.legacyContext }}</pre>
      </details>
    }
  </article>
}

@if (hasNext()) {
  <button rktButton btnType="secondary" type="button" class="consent-history-load-more" [disabled]="loading()" (click)="loadMore()">
    {{ loading() ? 'Loading...' : 'Load more' }}
  </button>
}
@if (failed() && events().length) {
  <div class="consent-history-message">Unable to load more consents. Try again later.</div>
}
